import React, { useState } from 'react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { LightBulbIcon, PlayCircleIcon } from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectNumberOfSteps, selectProjectPath, selectBranch, setCurrentStep } from 'app/sharedSlice';
import { getConfig } from 'config/config-helper';
import { sendMetrics, MetricType, ExitTo } from 'utils/metrics';

const { dataopsliveBaseUrl } = getConfig();

// Loom video URL
const LOOM_BASE_URL = 'https://www.loom.com/embed/2696e4451fd44c4899f3c4f2a1cc6b87';
const LOOM_PARAMS =
  'sid=bbc4be84-b81f-4c28-86ff-348674cea8c6&hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true';

export default function DbtFlowComplete(): JSX.Element {
  const dispatch = useAppDispatch();
  const numberOfSteps = useAppSelector(selectNumberOfSteps);
  const projectPath = useAppSelector(selectProjectPath);
  const branch = useAppSelector(selectBranch);
  const [videoStarted, setVideoStarted] = useState(false);

  const pathForLink = projectPath.toLowerCase().split(' ').join('-');
  const devReadyLink = `https://develop.dataops.live/quickstart?autostart=true#/https://app.dataops.live/${pathForLink}/-/tree/${branch}/`;
  const dataOpsProjectLink = `${dataopsliveBaseUrl}/${pathForLink}/-/tree/${branch}`;

  const sendMetricsAndNavigate = (exitTo: ExitTo): void => {
    sendMetrics(MetricType.create_dbt_end, null, exitTo);
  };

  // Function to track video start
  const handlePlayVideo = (): void => {
    // Track the video start
    sendMetrics(MetricType.create_dbt_video_view, null);
    console.log('Video start tracked');

    // Mark video as started to update the iframe src with autoplay
    setVideoStarted(true);
  };

  // Construct the iframe URL based on whether the video has started
  const iframeSrc = videoStarted ? `${LOOM_BASE_URL}?${LOOM_PARAMS}&autoplay=1` : `${LOOM_BASE_URL}?${LOOM_PARAMS}`;

  return (
    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
      <div className="relative w-[38rem] flex flex-col space-y-3 transform overflow-hidden bg-white px-4 pb-4 pt-5 text-left transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-dataops-forest-green/10">
          <CheckIcon aria-hidden="true" className="size-6 text-dataops-forest-green" />
        </div>
        <div className="text-base font-semibold text-gray-900 text-center">Migration successful</div>
        <p className="w-full text-center mt-1 text-sm text-gray-600">
          Congratulations, you have just created your first DataOps project from your dbt core project. To learn more
          about the fundamental DataOps concepts, check out the following links:
        </p>
        <div className="mt-2 flex gap-2 flex-wrap">
          <a
            href="https://docs.dataops.live/docs/guides/dataops-fundamentals/project-structure/"
            target="_blank"
            rel="noreferrer"
          >
            <button
              type="button"
              className="flex rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-dataops-primary-blue shadow-sm ring-1 ring-inset ring-dataops-primary-blue hover:text-hover-primary-blue"
            >
              <div className="flex-shrink-0">
                <LightBulbIcon className="h-5 w-5" aria-hidden="true" />
              </div>

              <div className="ml-1 flex-1 justify-between">
                <p className="text-sm">DataOps projects and pipelines</p>
              </div>
            </button>
          </a>
          <a
            href="https://docs.dataops.live/docs/dataops-development-environment/"
            target="_blank"
            rel="noreferrer"
            className="hidden xl:block"
          >
            <button
              type="button"
              className="flex rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-dataops-primary-blue shadow-sm ring-1 ring-inset ring-dataops-primary-blue hover:text-hover-primary-blue"
            >
              <div className="flex-shrink-0">
                <LightBulbIcon className="h-5 w-5" aria-hidden="true" />
              </div>

              <div className="ml-1 flex-1 justify-between">
                <p className="text-sm">Learn about the DataOps.live IDE</p>
              </div>
            </button>
          </a>
        </div>
        <p className="pt-4 text-sm text-gray-600 text-center">
          You&apos;re now ready to leverage our Cloud IDE to build and test your dbt models. Watch the video below to
          learn more about the Dynamic Transformation flow, then click the button below to continue your journey with
          DataOps.live.
        </p>

        {/* Loom Video Section */}
        <div className="w-full pt-4 flex flex-col items-center">
          <div className="relative w-full aspect-video rounded-md overflow-hidden shadow-md">
            {/* Loom iframe with dynamic src based on videoStarted state */}
            <iframe
              key={videoStarted ? 'playing' : 'paused'} // Force iframe refresh when src changes
              src={iframeSrc}
              frameBorder="0"
              allowFullScreen
              className="absolute top-0 left-0 w-full h-full"
              title="Dynamic Transformation Tutorial"
            ></iframe>

            {/* Custom play button overlay */}
            {!videoStarted && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-30 cursor-pointer hover:bg-opacity-20 transition-all duration-200"
                onClick={handlePlayVideo}
              >
                <div className="w-20 h-20 rounded-full bg-white bg-opacity-80 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 text-dataops-primary-blue"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>
          <div className="mt-2 text-sm text-gray-500 flex items-center">
            <PlayCircleIcon className="h-4 w-4 mr-1" />
            Watch this video to learn more about Dynamic Transformation
          </div>
        </div>
        <div className="w-full pt-4 flex justify-center space-x-2">
          <a
            href={devReadyLink}
            onClick={() => sendMetricsAndNavigate(ExitTo.developer)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="w-[12rem] rounded-md bg-gradient-to-r from-dataops-amethyst-lilac to-dataops-crayola-red hover:from-dataops-primary-blue hover:to-dataops-primary-blue px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-200 flex items-center justify-center group">
              Open Develop <span className="ml-2 transition-transform duration-200 group-hover:-translate-x-1">→</span>
            </button>
          </a>
          <a
            href={dataOpsProjectLink}
            className="w-[12rem]"
            onClick={() => sendMetricsAndNavigate(ExitTo.platform_repo)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="w-[12rem] rounded-md bg-gradient-to-r from-dataops-amethyst-lilac to-dataops-crayola-red hover:from-dataops-primary-blue hover:to-dataops-primary-blue p-[1px] text-sm font-semibold shadow-sm transition-all duration-200 group">
              <div className="rounded-md bg-white px-4 py-2 flex items-center justify-center">
                <span className="bg-clip-text text-gray-900 bg-gradient-to-r from-dataops-amethyst-lilac to-dataops-crayola-red">
                  Open Project
                </span>
                <span className="ml-2 transition-transform duration-200 group-hover:-translate-x-1 bg-clip-text text-gray-900 bg-gradient-to-r from-dataops-amethyst-lilac to-dataops-crayola-red">
                  →
                </span>
              </div>
            </button>
          </a>
        </div>
        <div className="absolute bottom-5 right-6 ml-[auto]">
          <button
            type="button"
            onClick={() => dispatch(setCurrentStep(numberOfSteps - 1))}
            className="text-sm leading-6 text-gray-900 hover:underline"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
}
